import { computed, defineAsyncComponent, defineComponent, nextTick, provide, ref, watch } from 'vue';
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { useAppModeStore } from 'src/stores/appMode';
import { useFlexStore } from 'src/stores/flex';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { stringParser as stringParserMethod } from 'src/plugins/string/string';
import { ChatListingPlugin } from 'src/features/messaging/chat/components/methods/queryListing';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import { ChatProcessMsgPlugin } from './components/methods/processMsgs';
import { ChatQueryPlugin } from './components/methods/queryMsgs';
import { ChatSendMsgPlugin } from './components/methods/sendMsg';
import { ChatStylePlugin } from 'src/features/messaging/chat/components/methods/chatStyle';
import { ChatTypingPlugin } from 'src/features/messaging/chat/components/methods/chatTyping';
import { ChatUploadPlugin } from './components/methods/uploadData';
import { merge } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import cloneDeep from 'lodash.clonedeep';
import chatHeader from './components/chat/header.vue';
import fullScreenImg from './components/chat/fullScreenImg.vue';
import generalDialog from 'src/components/general/dialog.vue';
import preUploadDialog from 'src/features/messaging/chat/components/chat/upload.vue';
import ChatInput from 'src/features/messaging/chat/components/input/chatInput.vue';
import report from './components/chat/report.vue';
export default defineComponent({
    name: 'chat-fb',
    components: {
        chatHeader,
        ChatInput,
        fullScreenImg,
        generalDialog,
        preUploadDialog,
        report,
        txtMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/txtMsg.vue')),
        fileMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/fileMsg.vue')),
        imgMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/imgMsg.vue')),
        listingMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/listingMsg.vue')),
        orderMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/orderMsg.vue')),
        videoMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/videoMsg.vue')),
        uploadingMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/uploadingMsg.vue')),
        uploadErrorMsg: defineAsyncComponent(() => import(
        /* webpackChunkName: "Messaging" */
        'src/features/messaging/chat/components/chat/messages/uploadErrorMsg.vue')),
        playVideo: defineAsyncComponent(() => import(
        /* webpackChunkName: "Listing" */
        'src/features/listing/components/video/playVideoDialog.vue')),
        hostDetailsDialog: defineAsyncComponent(() => import(
        /* webpackChunkName: "Details Dialog" */
        'src/features/host/flex/booking/components/detailsDialog.vue')),
        userDetailsDialog: defineAsyncComponent(() => import(
        /* webpackChunkName: "Details Dialog" */
        'src/features/account/flex/orders/components/detailsDialog.vue')),
        customOrder: defineAsyncComponent(() => import(
        /* webpackChunkName: "Custom Order" */
        'src/features/order/customOrder.vue'))
    },
    props: {
        backLabel: {
            type: String,
            default: 'Inbox'
        }
    },
    emits: ['close-chat', 'back'],
    setup(props, context) {
        const $q = useQuasar();
        const appModeStore = useAppModeStore();
        const flexStore = useFlexStore();
        provide('disableToggleDrawer', true);
        const { innerHeight, styleType, thumbStyle } = AppGeneralVariables();
        const disclaimer = 'Always keep your communication and transction on GetSpaces to ensure the best experience. Communication and transaction records will form the basis of dispute resolution.';
        const chatInputRef = ref(null);
        /**
         * General variables
         */
        const { messagewidth, user, convo, liveConvo, appType, isChatUser, receiver, chatBg, chatTxt, isSender } = ChatMiscDataPlugin();
        const routeString = computed(() => {
            if (!liveConvo.value[convo.value.id])
                return '';
            const convoData = liveConvo.value[convo.value.id];
            return stringParserMethod(convoData.listing_id.toString(), convo.value.listing_name);
        });
        /******
         * Put space ID in storage for calendar popup
         * to know which space to query
         ****/
        watch(liveConvo, (newValue) => {
            const convoData = newValue[convo.value.id];
            if (!convoData)
                return;
            $q.sessionStorage.set('calStore', {
                ...$q.sessionStorage.getItem('calStore') || {},
                listingId: convoData.listing_id.toString()
            });
        });
        /**
         * Typing
         */
        const { updateTyping } = ChatTypingPlugin();
        /**
         * Chat style and classes
         */
        const { imageWidth, contentRef, chatInputHeight, chatAreaHeightClass, updateInputHeight } = ChatStylePlugin();
        /**
         * Query chat data
         */
        function backFunc() {
            context.emit('back');
        }
        const { lastMsg, replied, initialized, loading, lastMsgId, noPrevMsgs, currPage, query$ } = ChatQueryPlugin(backFunc);
        /**
         * Processed Msgs
         */
        const { processedMsg } = ChatProcessMsgPlugin();
        /**
         * Send Msg
         */
        const isFirstMsg = computed(() => processedMsg.value.length === 0);
        const { consolidateSend, sendTxt } = ChatSendMsgPlugin(updateTyping, isFirstMsg, lastMsg, replied);
        /**
         * Scroll related
         */
        const chatAreaRef = ref(null);
        /**
         * When chat is scrolled, save scroll
         *
         * if it hits the top of the scroll area,
         * query previous msgs
         *
         * @param info scroll info
         */
        function scrollHandler(info) {
            if ((info.position.top > 0) || (info.direction !== 'up'))
                return;
            if (noPrevMsgs.value)
                return;
            query$.next(currPage.value);
            lastMsgId.value = currPage.value && processedMsg.value[0]
                ? processedMsg.value[0].messages[0].id || null
                : null;
        }
        const { subject: delayScrollPos$ } = rxInitSubjectCallback();
        useObservable(delayScrollPos$.pipe(debounceTime(100), tap(pos => {
            chatAreaRef.value?.setScrollPosition('vertical', pos, 200);
        })));
        // toggle scroll when keyboard shows and hides to keep the scroll consistent
        // web only
        watch(innerHeight, (newValue, oldValue) => {
            if (!chatAreaRef.value)
                return;
            if ($q.platform.is.capacitor)
                return;
            const difference = oldValue - newValue;
            const currentPosition = chatAreaRef.value.getScrollPosition().top;
            delayScrollPos$.next(currentPosition + difference);
        });
        // toggle scroll when keyboard shows and hides to keep the scroll consistent
        // app only
        const keyboardPreShowHt = computed(() => appModeStore.keyboardPreShowHt);
        watch(keyboardPreShowHt, (newValue, oldValue) => {
            if (!chatAreaRef.value)
                return;
            if (!$q.platform.is.capacitor)
                return;
            // if position is at the bottom, do nothing
            const posPercentage = chatAreaRef.value.getScrollPercentage().top;
            if (posPercentage === 1)
                return;
            const currentPosition = chatAreaRef.value.getScrollPosition().top;
            let scrollOffset = newValue > 0
                ? newValue
                : -oldValue;
            // ios special
            if ($q.platform.is.ios) {
                if (scrollOffset > 0) {
                    scrollOffset = chatInputHeight.value;
                }
                else {
                    scrollOffset = 0;
                }
            }
            delayScrollPos$.next(currentPosition + scrollOffset);
        });
        /**
         * Scroll all the way to be bottom. usually done on first query
         */
        function scrollBottomFunc() {
            chatAreaRef.value?.setScrollPosition('vertical', 1000000);
        }
        const { subject: delayScrollBottom$ } = rxInitSubjectCallback();
        useObservable(delayScrollBottom$.pipe(debounceTime(200), tap(() => {
            scrollBottomFunc();
        })));
        const references = ref({});
        const { subject: stickOrScroll$ } = rxInitSubjectCallback();
        const { subject: debouncedStickOrScroll$ } = rxInitSubjectCallback();
        useObservable(merge(stickOrScroll$, debouncedStickOrScroll$.pipe(debounceTime(50))).pipe(tap(() => {
            // after dom update completes, scroll to pos
            nextTick(() => {
                // if there is a last msg id saved,
                // i.e user has scrolled to the position before
                // stick to the position
                if (lastMsgId.value !== null) {
                    references.value[`msg-${lastMsgId.value}`].scrollIntoView(true);
                    lastMsgId.value = null;
                }
                else {
                    // else scroll to bottom
                    scrollBottomFunc();
                }
            });
        })));
        const appTab = computed(() => appModeStore.appTab);
        watch(appTab, (newValue, oldValue) => {
            if ((styleType.value === 'app') &&
                (newValue !== oldValue) &&
                (newValue === 'chatTab')) {
                debouncedStickOrScroll$.next(true);
            }
        });
        watch(initialized, (newValue, oldValue) => {
            if (!newValue || oldValue)
                return;
            debouncedStickOrScroll$.next(true);
        });
        watch(processedMsg, () => {
            stickOrScroll$.next(true);
        });
        /**
         * Listing
         */
        const { listing, listingIsActive, otherListingCount, videoCount } = ChatListingPlugin();
        /**
         * Order related
         */
        const customOrderId = ref(null);
        const showDetails = ref(false);
        /**
         * Close custom order details dialog
         */
        function closeTempOrderDetails() {
            customOrderId.value = null;
            showDetails.value = false;
        }
        /**
         * Show order details dialog
         * @param orderId order id
         */
        function showTempOrderDetails(orderId) {
            customOrderId.value = orderId;
            showDetails.value = true;
        }
        /**
         * When custom order is canceled by the host
         * @param dateIndex date index
         * @param msgIndex msg index
         */
        function cancelTempOrder(dateIndex, msgIndex) {
            processedMsg.value[dateIndex].messages[msgIndex].status = 'canceled';
        }
        /**
         * Create custom order
         */
        const customListingId = ref(null);
        const customOrderDialog = computed({
            get: () => {
                return flexStore.customOrderDialog;
            },
            set: (newVal) => {
                flexStore.setCustomOrderDialog(newVal);
            }
        });
        /**
         * Close custom order popup
         */
        function closeCustomOrder() {
            customListingId.value = null;
            customOrderDialog.value = false;
        }
        const listingInactiveDialog = ref(false);
        /**
         * Show custom order popup
         */
        function showCustomOrder() {
            if (!listingIsActive.value.active ||
                !listingIsActive.value.approved) {
                if (listingIsActive.value.active === false ||
                    listingIsActive.value.approved === false) {
                    listingInactiveDialog.value = true;
                }
                return;
            }
            const convoVal = cloneDeep(convo.value);
            const convoData = cloneDeep(liveConvo.value[convoVal.id]);
            customListingId.value = convoData.listing_id.toString();
            customOrderDialog.value = true;
        }
        /**
         * After custom order is created by the host,
         * send a custom order msg using data returned
         * @param data {
         *  img: space image url
         *  id: custom order id
         * }
         */
        function tempOrderCreated(data) {
            consolidateSend({
                type: 'custom_order',
                text: '',
                url: [data.img],
                status: 'open',
                custom_order_id: data.id,
                ending: [],
                filename: [],
                conversationId: convo.value.id,
                sender: Number(user.value.user_pk),
                receiver: Number(receiver.value)
            });
            chatInputRef.value?.closeUploadMenu();
            closeCustomOrder();
        }
        /******
         * Image
         ******/
        const fileSrc = ref('');
        const fullScreen = ref(false);
        /**
         * Show image in full screen
         * @param value url
         */
        function enlargePic(value) {
            fileSrc.value = value;
            fullScreen.value = true;
        }
        /******
         * Video
         *****/
        const playVideoUrl = ref('');
        const videoDialogShow = ref(false);
        /**
         * Hide video popup
         */
        function hideVideo() {
            videoDialogShow.value = false;
            playVideoUrl.value = '';
        }
        /**
         * show video popup
         * @param url youtube url
         */
        function showVideo(url) {
            videoDialogShow.value = true;
            playVideoUrl.value = url;
        }
        /**
         * Upload
         */
        const preUploadDialogRef = ref(null);
        /**
         * Before file is uploaded
         * @param fileData file data
         * @param type type of file
         */
        function beforeUpload(fileData, type) {
            updateTyping(true);
            preUploadDialogRef.value?.beforeUpload(fileData, type);
        }
        /**
         * Clear file input for users to click and upload again
         */
        function clearComponentData() {
            preUploadDialogRef.value?.clearData(false);
            chatInputRef.value?.clearFileInput();
            chatInputRef.value?.closeUploadMenu();
        }
        /**
         * After file is sent, send message with file data in it
         * @param data uploaded file data
         */
        function uploadedCallback(data) {
            updateTyping(false);
            consolidateSend({
                dimensions: data.dimensions,
                type: data.type,
                text: data.text,
                url: data.url,
                ending: [data.ending],
                filename: [data.filename],
                conversationId: convo.value.id,
                sender: Number(user.value.user_pk),
                receiver: Number(receiver.value),
            });
            clearComponentData();
        }
        const { uploadProgress, errorStore, uploadQueue, upload$, reupload$ } = ChatUploadPlugin(() => {
            updateTyping(false);
            clearComponentData();
        }, () => {
            clearComponentData();
            delayScrollBottom$.next('');
        }, uploadedCallback);
        /**
         * Pre exit
         */
        const chatHeadRef = ref(null);
        /**
         * Check if there are pending messages sent.
         * if there is, wait till sending msgs are completed
         * before exiting
         */
        function checkSend() {
            return chatHeadRef.value?.checkSend();
        }
        return {
            styleType,
            thumbStyle,
            disclaimer,
            // component ref
            chatInputRef,
            // general variables
            messagewidth,
            convo,
            appType,
            isChatUser,
            receiver,
            chatBg,
            chatTxt,
            isSender,
            routeString,
            // chat style and classes
            imageWidth,
            contentRef,
            chatAreaHeightClass,
            updateInputHeight,
            // query
            loading,
            noPrevMsgs,
            // process msgs
            processedMsg,
            // send
            sendTxt,
            // scroll
            chatAreaRef,
            references,
            scrollHandler,
            // listing
            listing,
            listingIsActive,
            otherListingCount,
            videoCount,
            // typing
            updateTyping,
            // custom order
            customOrderId,
            showDetails,
            cancelTempOrder,
            closeTempOrderDetails,
            showTempOrderDetails,
            listingInactiveDialog,
            // create custom order
            customListingId,
            customOrderDialog,
            closeCustomOrder,
            showCustomOrder,
            tempOrderCreated,
            // image
            fileSrc,
            fullScreen,
            enlargePic,
            // video
            playVideoUrl,
            videoDialogShow,
            hideVideo,
            showVideo,
            // uploads
            preUploadDialogRef,
            beforeUpload,
            errorStore,
            uploadQueue,
            uploadProgress,
            upload$,
            reupload$,
            // pre exit
            chatHeadRef,
            checkSend
        };
    }
});
